import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AzureKeyVaultLinkedService } from 'app/model/linkedservice/azure-key-vault.linkedservice';
import { ValueAdapter } from '../value.adapter';
import { azureKeyVaultAuthTypes, ReferenceAdapter, Credential, CredentialTypeOptions, azureKeyVaultAuthRefTypes, ITridentResource } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { AuthConstants } from 'app/services/authentication/auth-constants';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureKeyVaultLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            typeProperties: {
                baseUrl: '*',
                credential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureKeyVaultLinkedServiceAdapter extends LinkedServiceAdapter<AzureKeyVaultLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureKeyVaultLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationMethod;
        if (model.credential) {
            const credType = model.credential.getType();
            if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = azureKeyVaultAuthTypes.MSIAuthentication;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = azureKeyVaultAuthTypes.UserAssignedManagedIdentity;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = azureKeyVaultAuthTypes.UserAuth;
            }
        }
        model.authenticationMethod = finalAuthType;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Model.AzureKeyVaultLinkedServiceTypeProperties => {
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        const pathProperties = datasourceReference.path.split(';');
        const typeProperties: Model.AzureKeyVaultLinkedServiceTypeProperties = {
            baseUrl : `https://${pathProperties[0]}.${AuthConstants.RESOURCE_DOMAIN_MAP.AzureKeyVault}`
        };
        return typeProperties;
    };

    protected modelConstructor(): new () => AzureKeyVaultLinkedService {
        return AzureKeyVaultLinkedService;
    }

    protected model(): IModel {
        return AzureKeyVaultLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureKeyVaultLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureKeyVaultLinkedService;

        properties.type = Metamodel.modelFor(AzureKeyVaultLinkedService).name;
        properties.typeProperties = {
            baseUrl: this.valueToResource(linkedService, AzureKeyVaultLinkedService.BASE_URL),
            credential: (((GlobalVariables.isSamiSupportEnabled && linkedService.authenticationMethod === azureKeyVaultAuthTypes.MSIAuthentication
                && linkedService.authRefMethod === azureKeyVaultAuthRefTypes.CredentialRefTypeAuthentication)
                || linkedService.authenticationMethod === azureKeyVaultAuthTypes.UserAssignedManagedIdentity
                || (GlobalVariables.enableConnectorUserAuthCred && linkedService.authenticationMethod === azureKeyVaultAuthTypes.UserAuth))
                && linkedService.credential) ?
                ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference :
                undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureKeyVaultLinkedService): Promise<AzureKeyVaultLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureKeyVaultLinkedService;
        linkedService.credential = undefined;
        // Clean old value
        AzureKeyVaultLinkedService.BASE_URL.setValue(linkedService, undefined);
        AzureKeyVaultLinkedService.BASE_URL.setTemplateValue(linkedService, undefined);
        if (properties && properties.typeProperties) {
            ValueAdapter.toModel(linkedService, AzureKeyVaultLinkedService.BASE_URL, properties.typeProperties.baseUrl);
            if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context);
                linkedService.authRefMethod = azureKeyVaultAuthRefTypes.CredentialRefTypeAuthentication;
            } else {
                linkedService.authenticationMethod = azureKeyVaultAuthTypes.MSIAuthentication;
                linkedService.authRefMethod = azureKeyVaultAuthRefTypes.InlineRefTypeAuthentication;
            }
        }
        return linkedService;
    }
}
